<template>
    <div>
        <BreadcrumbsProject
        v-if="!isDeluxeLayout"
        :breadcrumbs="[
            {
                link : '/high-level-estimates',
                title : $t('Bidding'),
                isRouterLink: true
            },
            {
                link : '/high-level-estimates/' + hleProjectId + '/quotation',
                title : (hleProject.projectName || '-' )+ ' - ' + $t('Bidding Module.High Level Estimates'),
                isRouterLink: true
            },
        ]"
        />
        <div class="card hle-bidding-card hle-estimate-card directory-list-card">
            <div class="card-header directory-list-card__header hle-bidding-card__header hle-estimate-card__header">
                <div class="directory-list-card__title hle-estimate-card__title">
                    <span class="hle-estimate-eyebrow">{{ hleProject.projectName || '-' }}</span>
                    <h4>{{ $t('Bidding Module.High Level Estimates') }}</h4>
                </div>
                <div class="hle-estimate-card__actions">
                    <div class="hle-estimate-mode-switch" role="group" :aria-label="$t('Bidding Module.High Level Estimates')">
                        <label :class="['hle-estimate-mode-switch__item', { active: viewMode == 1 }]" for="view-mode-1">
                            <input class="visually-hidden" type="radio" value="1" v-model="viewMode" id="view-mode-1" />
                            {{ $t('Bidding Module.High Level Estimates') }}
                        </label>
                        <label :class="['hle-estimate-mode-switch__item', { active: viewMode == 2 }]" for="view-mode-2">
                            <input class="visually-hidden" type="radio" value="2" v-model="viewMode" id="view-mode-2" />
                            {{ $t('Bidding Module.Detailed Estimates')}}
                        </label>
                    </div>
                    <button class="btn btn-primary directory-add-btn hle-bidding-add-btn" data-bs-toggle="modal" data-bs-target="#QuotationModalAdd" @click="OpenCreateQuotation">
                        <i class="fa-solid fa-plus"></i>
                        {{ $t('Add')}}
                    </button>
                </div>
            </div>
            <div class="card-body directory-list-card__body">
                <div class="directory-list-toolbar hle-bidding-toolbar hle-estimate-toolbar">
                    <div class="directory-search hle-bidding-search">
                        <i class="fa-solid fa-magnifying-glass"></i>
                        <input type="search" class="form-control" v-model="searchBar" :placeholder="$t('Bidding Module.Search Name of High Level Estimates here')" />
                    </div>
                    <div class="directory-pagination hle-bidding-pagination" v-if="quotationList.totalPages">
                        <Pagination
                            :changePage="PageChange"
                            :paginationDetails="{totalPages : quotationList.totalPages, page : quotationList.page || 1}"
                        />
                    </div>
                </div>
                <div class="table-responsive">
                    <table class="table table-striped directory-table hle-bidding-table hle-estimate-table">
                        <thead>
                            <tr>
                                <th> {{ $t('Name')}}</th>
                                <th class="hle-estimate-table__amount"> {{ $t('Total')}}</th>
                                <th> {{ $t('Created By')}}</th>
                                <th> {{ $t('Created At')}}</th>
                                <th class="hle-bidding-table__status"> {{ $t('Status')}}</th>
                                <th class="hle-bidding-table__actions-heading">{{ $t('Actions') }}</th>
                            </tr>
                        </thead>
                        <tbody v-if="!isLoaded">
                            <tr>
                                <td colspan="6" class="hle-bidding-loader">
                                    <Loader />
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-else>
                            <tr v-if="quotationList.docs.length==0">
                                <td colspan="6" class="text-center directory-empty-state">
                                    {{ $t('No data Found')}}
                                </td>
                            </tr>
                            <tr v-for="(item,key) in quotationList.docs" :key="key" >
                                <td>
                                    <router-link :to="{name : 'HleQuotation', params : {id : hleProjectId, qtnId : item._id} }" class="text-capitalize directory-user-name hle-bidding-name">
                                        {{item.name}}
                                    </router-link>
                                </td>
                                <td class="hle-estimate-table__amount" :title="$t('Quotation Currency is') + ' ' + (item.currencyId?.name || '-')">{{item.currencyId?.sign}}{{ReformatNumber(item.amountTotal)}}</td>
                                <td class="text-capitalize">{{item.createdBy?.fullname}}</td>
                                <td>{{ReformatDate(item.createdAt)}}</td>
                                <td class="hle-bidding-table__status">
                                    <span :class="['badge hle-bidding-status-pill', GetStatusClass(item.status)]">{{ GetStatusName(item.status) }}</span>
                                </td>
                                <td class="directory-table__actions hle-bidding-table__actions hle-estimate-table__actions">
                                    <router-link :to="{name : 'HleQuotation', params : {id : hleProjectId, qtnId : item._id}  }" class="directory-icon-btn hle-bidding-icon-btn" :title="$t('View')">
                                        <i class="fa-solid fa-magnifying-glass"></i>
                                    </router-link>
                                    <router-link v-if="item.status!=ApprovalStageBoq.DRAFT" :to="{name : 'HleQuotationHistory', params : {id : hleProjectId, qtnId : item._id } }" class="directory-icon-btn hle-bidding-icon-btn" :title="$t('History')">
                                        <i class="fa-solid fa-clock-rotate-left"></i>
                                    </router-link>
                                    <button v-if="item.status==ApprovalStageBoq.DRAFT" class="directory-icon-btn directory-icon-btn--delete hle-bidding-icon-btn" @click="DeleteQuotation(item._id,item.name)" :title="$t('Delete')">
                                        <i class="fa-solid fa-trash-can"></i>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>   
                </div>
                <div class="hle-bidding-page-count" v-if="quotationList.totalPages">
                    <small>{{ $t('Showing page') }} {{quotationList.page || 1}} {{ $t('of') }} {{quotationList.totalPages}}</small>
                </div>
            </div>
        </div>
        <Modal idModal="QuotationModalAdd" :headerTitle="$t('Bidding Module.Create High Level Estimate')" modalSize="modal-md" >
            <div class="form-group">
                <label>
                    {{ $t('Name') }}
                    <span class="bidding-required-inline" v-if="!HasRequiredValue(newQuotation.name)">({{ $t('Required').toLowerCase() }})</span>
                </label>
                <input class="form-control" :placeholder="$t('Bidding Module.Name of the High Level Estimate')" v-model="newQuotation.name" />
            </div>
            <!-- <div class="form-group">
                <label class="required-style"> Currency  </label>
                <select class="form-select" v-model="newQuotation.currencyId" >
                    <option value="">Select a Currency</option>
                    <option v-for="(item, key) in boqCurrency" :key="key" :value="item._id">{{item.sign}} {{item.name}} </option>
                </select>
                <small class="text-warning" v-if="isSaveQuotationClicked && !newQuotation.currencyId">Required</small>
            </div> -->
            <template v-slot:actions>
                <button type="button" ref="closeQuotationModalAdd" :class="isDeluxeLayout ? 'custom-btn custom-btn--cancel' : 'btn btn-outline-danger'" data-bs-dismiss="modal">{{ isDeluxeLayout ? $t('Cancel') : 'X ' + $t('Cancel') }}</button>
                <button type="button" :class="isDeluxeLayout ? 'custom-btn' : 'btn btn-primary'" :disabled="isOnProcess || !isCreateQuotationReady" @click="CreateQuotation($event.target)" ><i v-if="!isDeluxeLayout" class="fas fa-save"></i> {{ $t('Save Changes') }}</button>
            </template>
        </Modal>
    </div>
</template>


<script>
import 'dotenv/config'
import BreadcrumbsProject  from '@/components/projectInformation/BreadcrumbsProject.vue'
import Pagination from '@/components/layout/Pagination.vue'
import Modal from '@/components/layout/Modal.vue'
import Loader from '@/components/layout/Loader.vue'
import  {mapActions,mapGetters} from 'vuex'
import {LinkList, ApprovalStageBoqObj,ReformatDate, ReformatNumber, ApprovalStageBoq,LocalStorageNameOfListByHLE} from '@/helpers/utilities.js'
import {GetHighLevelQuotationsList,AddHighLevelQuotation,DeleteHighLevelQuotation, GetHighLevelProject} from '@/actions/boqActions.js'
export default {
    name : 'HleList',
    components : {
        BreadcrumbsProject, Pagination, Loader, Modal
    },
    computed : {
        ...mapGetters(['boqCurrency']),
        isDeluxeLayout(){
            return this.$route.meta.layout == 'deluxe'
        },
        isCreateQuotationReady(){
            return this.HasRequiredValue(this.newQuotation.name)
        }
    },
    mounted : async function(){
        const self = this
        self.ChangeActiveLink(LinkList.HIGH_LEVEL_ESTIMATES)
        const listBy = window.localStorage.getItem(LocalStorageNameOfListByHLE) || 1
        self.quotationListParams.listBy = listBy
        self.selectedCreatedBy = self.creatorOptions.find(u=>u.id==listBy) 
        self.quotationList = await GetHighLevelQuotationsList(self.quotationListParams) 
        await self.GetCurrencyListRun()
        self.hleProject = await GetHighLevelProject(self.hleProjectId)
        if(!self.hleProject){
            self.$router.push('/unauthorized')
            return
        }
        self.isLoaded = true
    },
    methods : {
        ...mapActions(['ChangeActiveLink','GetCurrencyListRun']),
        HasRequiredValue(value){
            return String(value || '').trim().length > 0
        },
        GetStatusName(status){
            const stage = ApprovalStageBoqObj[Number(status) - 1]
            if(!stage){
                return '-'
            }
            return String(stage.name).toLowerCase().replace(/\b\w/g, (char)=>char.toUpperCase())
        },
        GetStatusClass(status){
            switch(Number(status)){
                case 1:
                case 2:
                case 3:
                    return 'hle-bidding-status-pill--pending'
                case 4:
                    return 'hle-bidding-status-pill--approval'
                case 5:
                case 8:
                    return 'hle-bidding-status-pill--approved'
                case 6:
                    return 'hle-bidding-status-pill--revision'
                default:
                    return 'hle-bidding-status-pill--neutral'
            }
        },
        async PageChange(page){
            const self = this
            self.quotationListParams.page = page
            self.quotationList = await GetHighLevelQuotationsList(self.quotationListParams) 
        },
        OpenCreateQuotation(){
            this.newQuotation = {
                name : ''
            }
        },
        async CreateQuotation(){
            const self = this

            if(self.isOnProcess){
                return
            }
            self.isSaveQuotationClicked = true
            const data = self.newQuotation
            data.hleProjectId = self.hleProjectId
            if(!self.isCreateQuotationReady){
                return
            }
            self.isSaveQuotationClicked = true
            self.isOnProcess = true

            const created = await AddHighLevelQuotation(data)
            self.isOnProcess = false
            if(created.quotationId)
            {
                self.$router.push({name : 'HleQuotation', params : { id : self.hleProjectId,  qtnId :created.quotationId }})
                self.$refs.closeQuotationModalAdd.click()
            }
            else
            {
                self.$swal(self.$i18n.t('Bidding Module.Create High Level Estimate'),created,'warning')
            }
        },
        DeleteQuotation (id,name){
            const self = this
            self.$swal({
                title: self.$i18n.t('Bidding Module.Delete High Level Estimates'),
                text : `"${name}" ${self.$i18n.t('Bidding Module.will be Deleted Permanently, Proceed?')}`,
                showCancelButton: true,
                icon: 'warning',
                confirmButtonText: self.$i18n.t('Delete'),
                cancelButtonText : self.$i18n.t('Cancel'),
                showLoaderOnConfirm : true,
                allowOutsideClick: () => !self.$swal.isLoading(),
                preConfirm : ()=>{
                    return DeleteHighLevelQuotation(id)
                }
            }).then(async (res)=>{
                if(res.isConfirmed){
                    if(res.value=='OK'){
                        self.quotationList = await GetHighLevelQuotationsList(self.quotationListParams) 
                        self.$swal(self.$i18n.t('Bidding Module.Delete High Level Estimates'),'','success')
                    }else{
                        self.$swal(self.$i18n.t('Bidding Module.Delete High Level Estimates'),res.value,'warning')
                    }
                
                }
            })
        },
        timerSetterSearch(callback){
            const self = this
            
            if(self.timerSearch){
                clearTimeout(self.timerSearch)
                self.timerSearch = null
            }

            self.timerSearch = setTimeout(callback,700)
        },
    },
    watch : {
        searchBar(val) {
            const self = this
            self.quotationListParams.page = 1
            self.quotationListParams.search = val
            self.timerSetterSearch(async function(){
                self.quotationList = await GetHighLevelQuotationsList(self.quotationListParams) 
            })
        },
        selectedCreatedBy(val) {
            const self = this
            if(!val){ 
                return 
            }
            self.quotationListParams.page = 1
            if(val)
                self.quotationListParams.listBy = val.id
            else 
                self.quotationListParams.listBy = undefined

            window.localStorage.setItem(LocalStorageNameOfListByHLE,val.id || 1)
            self.timerSetterSearch(async function(){
                self.quotationList = await GetHighLevelQuotationsList(self.quotationListParams) 
            })
        },
        viewMode(val){
            let viewMode = val
            if(viewMode==1)
                this.$router.push({name : 'HleList', params : {id : this.hleProjectId}})
            else
                this.$router.push({name : 'HleBoqs', params : {id : this.hleProjectId}})
        }
        
    },
    data(){
        return {
            viewMode : 1,
            hleProject : {},
            hleProjectId : this.$route.params.id,
            boqUrl : process.env.VUE_APP_BOQ_URL,
            quotationList : {
                docs : [],
            },
            quotationListParams : {
                listBy : 1,
                page : 1,
                search : '',
                hleProjectId : this.$route.params.id
            },
            searchBar : '',
            selectedCreatedBy : { id : 1, value : 'My High Level Estimates'},
            creatorOptions  : [ 
                { id : 1, value : 'My High Level Estimates'},
                { id : 2, value : 'Bidding High Level Estimates'},
            ],
            timerSearch :null,
            ApprovalStageBoqObj,
            ApprovalStageBoq,
            ReformatDate,
            ReformatNumber,
            isLoaded : false,
            isOnProcess : false,
            newQuotation : {},
            isSaveQuotationClicked : false,
            UpdateQuotation : {},
            UpdateQuotationClicked : false,
        }
    },
}
</script>
